/**
 * Offline persistence — TASK 10.1 Step 2.
 *
 * ONE module owns every byte the app keeps on the device for offline use, so
 * `lib/local-purge.ts` has exactly one thing to erase and this file has exactly
 * one place to get the names right. Every database and cache name below is
 * IMPORTED from `local-purge.ts` (its module header makes that a contract:
 * "TASK 10.1 must name its IndexedDB database `${OWNED_DB_PREFIX}<something>`
 * and its runtime caches `${USER_DATA_CACHE_PREFIX}<something>`, importing
 * these constants rather than re-declaring the strings — otherwise its
 * downloads survive a purge"). `apps/web/test/offline-store.test.ts` seeds
 * every backend, runs the real `purgeLocalState`, and reads them back empty.
 *
 * ── WHERE EACH KIND OF BYTE LIVES, AND WHY ────────────────────────────────
 *
 *   | data                        | backend                         |
 *   |-----------------------------|---------------------------------|
 *   | FINISHED audio bodies       | Cache Storage {@link AUDIO_CACHE_NAME} |
 *   | FINISHED cover art          | Cache Storage {@link ART_CACHE_NAME}   |
 *   | in-flight partial bytes     | IndexedDB `partials`            |
 *   | download records + progress | IndexedDB `tracks`              |
 *   | Downloads-view catalog rows | IndexedDB `items`               |
 *   | best-effort catalog cache   | IndexedDB `catalog` (LRU)       |
 *   | client telemetry queue (W5) | IndexedDB `telemetry`           |
 *   | offline PlayEvents (AC 13.7)| IndexedDB `playEvents`          |
 *
 * The dev plan's file list says "IndexedDB: audio blobs"; Security N1's own
 * parenthetical sanctions either backend ("cache-backed responses (Cache
 * Storage / IndexedDB)"). FINISHED bodies live in Cache Storage for one
 * load-bearing reason: **`cache.put()` is atomic**. A `Response` only enters
 * the cache once the whole body has been assembled, so a partially downloaded
 * track is not merely *flagged* unplayable (AC 13.4) — it is STRUCTURALLY
 * absent from the only place the service worker ever looks. A half-written IDB
 * blob would need a correctness flag consulted on every read, and a flag can be
 * wrong. Partial bytes accumulate in a SEPARATE IDB store the SW never reads.
 * Both cache names carry {@link USER_DATA_CACHE_PREFIX}, so the purge erases
 * them exactly as it erases the databases.
 *
 * ── NO `blob:` URLS ───────────────────────────────────────────────────────
 * Nothing here ever calls `URL.createObjectURL`. Offline audio and art are
 * delivered by the service worker answering the app's ordinary same-origin
 * `/api/stream/*` and `/api/art/*` requests from these caches, so CSP
 * `media-src`/`img-src` stay `'self'` (TASK 2.6 / Security N1).
 * `apps/web/test/offline-no-blob-urls.test.ts` greps the source for the sink.
 *
 * ── BOUNDED, LIKE EVERYTHING ELSE HERE ────────────────────────────────────
 * The catalog cache is LRU-evicted at {@link MAX_CATALOG_ENTRIES}, the
 * telemetry queue at `telemetry-client.ts`'s own `MAX_QUEUED`, the offline
 * PlayEvent queue at {@link MAX_PLAY_EVENTS}, and the Downloads list at
 * {@link MAX_DOWNLOAD_ITEMS}. All four drop the OLDEST entries, so a long
 * offline session degrades rather than exhausting the origin's quota.
 *
 * ── THE PURGE MUST NOT BE BLOCKED BY THIS MODULE ──────────────────────────
 * The connection is cached (this store is read on every catalog paint), which
 * would make `indexedDB.deleteDatabase` fire `blocked` — a failure `localPurge`
 * reports and gives up on. So the open connection listens for `versionchange`
 * and closes itself, which is what lets the purge complete on the first try.
 *
 * ── NO PII, ANYWHERE ──────────────────────────────────────────────────────
 * Cache keys are the app's own same-origin URLs; IDB keys are catalog UUIDs.
 * Nothing here logs a stored value, and the telemetry buffer stores exactly
 * what `telemetry-client.ts` already sanitised through its allowlist.
 *
 * @module
 */

import type { PlayEventInput } from '@streamymusic/shared';

import { ART_CACHE_NAME, AUDIO_CACHE_NAME, OFFLINE_DB_NAME } from './local-purge.js';
import {
  MAX_QUEUED,
  type BufferedTelemetryEvent,
  type TelemetryBuffer,
} from './telemetry-client.js';

/* ── Names (all derived from local-purge.ts) ───────────────────────────── */

/**
 * Schema version of {@link OFFLINE_DB_NAME}.
 *
 * ── v1 → v2: THE PARTIALS STORE WAS REBUILT (Performance C2) ──────────────
 * v1 keyed `partials` by `trackId` and held ONE row per track containing a
 * `parts: ArrayBuffer[]` array. Appending a chunk meant `get` (structured-clone
 * a copy of every byte received so far), `push`, `put` (clone it all back) — so
 * an N-MiB track moved ≈N²/2 MiB in each direction. A 100 MB FLAC cost ~5 GB of
 * read plus ~5 GB of write to store 100 MB, and held the whole deserialized
 * array on the JS heap once per flush.
 *
 * v2 stores ONE RECORD PER CHUNK under a compound string key, so an append is a
 * single `add` of a single chunk and is O(1) in the bytes already received.
 * See {@link partialChunkKey}.
 *
 * MIGRATION POLICY — v1 partials are DISCARDED, not converted. They are the one
 * kind of data in this database that is losslessly re-derivable: a partial is
 * by definition an unfinished download, `transfer()` reads `partialLength()`
 * and simply restarts from `0` when it is empty, and the only cost is
 * re-fetching bytes the user never had a playable track for. Converting them
 * would mean running the O(n²) read path one last time, on boot, on exactly the
 * largest buffers — which is the thing this migration exists to stop. Finished
 * audio is untouched: it lives in Cache Storage, not here.
 */
export const OFFLINE_DB_VERSION = 2;

export const STORE_ITEMS = 'items';
export const STORE_TRACKS = 'tracks';
export const STORE_PARTIALS = 'partials';
export const STORE_CATALOG = 'catalog';
export const STORE_TELEMETRY = 'telemetry';
export const STORE_PLAY_EVENTS = 'playEvents';

/** Every object store, created together on upgrade. */
export const OFFLINE_DB_STORES: readonly string[] = [
  STORE_ITEMS,
  STORE_TRACKS,
  STORE_PARTIALS,
  STORE_CATALOG,
  STORE_TELEMETRY,
  STORE_PLAY_EVENTS,
];

/**
 * Re-exported from `local-purge.ts` (which owns every storage name — see its
 * header) so a caller of this module needs one import, not two.
 */
export { ART_CACHE_NAME, AUDIO_CACHE_NAME } from './local-purge.js';

/* ── Bounds ────────────────────────────────────────────────────────────── */

/** LRU ceiling on the best-effort catalog cache. */
export const MAX_CATALOG_ENTRIES = 200;
/** Ceiling on locally-captured offline PlayEvents (oldest dropped). */
export const MAX_PLAY_EVENTS = 500;
/** Ceiling on rows in the Downloads view (oldest dropped). */
export const MAX_DOWNLOAD_ITEMS = 500;

/* ── Partial-buffer keys (Performance C2) ──────────────────────────────── */

/**
 * Width the chunk sequence is zero-padded to inside {@link partialChunkKey}.
 *
 * Keys are STRINGS, so IndexedDB orders them lexically — `"…:10"` would sort
 * before `"…:2"` and `commitAudio` would concatenate the body out of order,
 * producing a corrupt track that still committed cleanly. Padding makes the
 * lexical order the numeric order. 12 digits is ~10¹² chunks: at
 * `PARTIAL_FLUSH_BYTES` (1 MiB) that is an exabyte per track, i.e. it cannot
 * overflow in practice, and {@link MAX_PARTIAL_CHUNKS} caps it long before.
 */
export const PARTIAL_SEQ_DIGITS = 12;

/**
 * Hard ceiling on chunks per track. At 1 MiB per flush this is a 64 GiB track
 * — far beyond any real audio file, so reaching it means something is wrong
 * (a server streaming an endless body). Bounded like everything else here.
 */
export const MAX_PARTIAL_CHUNKS = 65_536;

/**
 * The per-track metadata record inside the partials store: how many bytes have
 * landed and which sequence number is next.
 *
 * It exists so {@link OfflineStore.partialLength} is an O(1) single-key read
 * instead of a sum over every chunk — the whole point of the v2 schema is that
 * NOTHING on the append path is proportional to the bytes already received.
 *
 * `#` (0x23) sorts before `:` (0x3A), so a track's meta record always precedes
 * its chunks in key order. The track id is percent-encoded, so an id that
 * itself contained `:` or `#` could not forge another track's key space.
 */
export function partialMetaKey(trackId: string): string {
  return `${encodeURIComponent(trackId)}#meta`;
}

/** Key of one chunk. See {@link PARTIAL_SEQ_DIGITS} for why it is padded. */
export function partialChunkKey(trackId: string, seq: number): string {
  return `${encodeURIComponent(trackId)}:${String(seq).padStart(PARTIAL_SEQ_DIGITS, '0')}`;
}

/** Prefix every key belonging to `trackId` starts with. */
export function partialKeyPrefix(trackId: string): string {
  return encodeURIComponent(trackId);
}

/** True when `key` belongs to `trackId` (meta or chunk), and to no other. */
export function isPartialKeyFor(key: unknown, trackId: string): boolean {
  if (typeof key !== 'string') return false;
  const prefix = partialKeyPrefix(trackId);
  // The separator is REQUIRED: without it `abc` would claim `abcd`'s keys.
  return key.startsWith(`${prefix}:`) || key === `${prefix}#meta`;
}

/** A chunk key (not the meta record) belonging to `trackId`. */
export function isPartialChunkKeyFor(key: unknown, trackId: string): boolean {
  return typeof key === 'string' && key.startsWith(`${partialKeyPrefix(trackId)}:`);
}

/**
 * Coerces a persisted chunk to an `ArrayBuffer`, or `null` if it is not one.
 *
 * ── WHY NOT `value instanceof ArrayBuffer` ────────────────────────────────
 * `instanceof` compares against the CURRENT REALM's constructor, and a value
 * that has been through IndexedDB's structured clone need not carry it. That
 * is routine in a jsdom test (the module and the suite hold different
 * `ArrayBuffer` identities) and it is reachable in a browser too — a buffer
 * that arrived from a Worker or another document has a foreign prototype. A
 * failed check here is SILENT AND TOTAL: the chunk is dropped, `commitAudio`
 * assembles a body with a hole in it or refuses outright, and nothing logs.
 *
 * Structural detection instead, with `ArrayBuffer.isView` handling the case
 * where the platform hands back a typed-array view rather than the raw buffer.
 */
function asArrayBuffer(value: unknown): ArrayBuffer | null {
  if (value instanceof ArrayBuffer) return value;
  if (ArrayBuffer.isView(value)) {
    // A COPY, not `view.buffer`: the view may cover a slice of a larger
    // buffer, and handing the whole thing back would silently widen the chunk.
    // `.buffer` is typed `ArrayBufferLike` (which admits `SharedArrayBuffer`),
    // so the copy also narrows the return type honestly.
    return new Uint8Array(new Uint8Array(value.buffer, value.byteOffset, value.byteLength)).buffer;
  }
  // Cross-realm: no shared prototype, but the shape is unmistakable.
  if (typeof value === 'object' && value !== null && 'byteLength' in value) {
    const candidate: { byteLength: unknown } = value;
    if (typeof candidate.byteLength === 'number') return value as ArrayBuffer;
  }
  return null;
}

interface PartialMeta {
  readonly bytes: number;
  readonly nextSeq: number;
}

function isPartialMeta(value: unknown): value is PartialMeta {
  return isRecord(value) && typeof value.bytes === 'number' && typeof value.nextSeq === 'number';
}

/* ── Record shapes ─────────────────────────────────────────────────────── */

/**
 * Per-track download lifecycle. `done` is asserted ONLY after
 * {@link OfflineStore.commitAudio} has put a complete body in Cache Storage —
 * every other value means "not playable offline" (AC 13.4).
 */
export type DownloadStatus = 'queued' | 'downloading' | 'resuming' | 'done' | 'failed';

/** What the Downloads view lists — one row per user-initiated download. */
export interface DownloadItem {
  readonly id: string;
  readonly type: 'Album' | 'Playlist' | 'Track';
  readonly title: string;
  /** The mock's secondary line ("Radiohead · 10 tracks"). */
  readonly sub: string;
  /** Album this row's art belongs to, when it has one. */
  readonly albumId: string | null;
  readonly trackIds: readonly string[];
  readonly createdAt: number;
}

/**
 * One track's download state AND the catalog metadata airplane-mode playback
 * needs (AC 13.2: title/artist/album + cover). Held here rather than fetched,
 * because the whole point is that no network is available when it is read.
 */
export interface DownloadTrack {
  readonly trackId: string;
  readonly itemId: string;
  readonly title: string;
  readonly artist: string;
  readonly album: string;
  readonly albumId: string;
  readonly durationS: number;
  readonly format: string;
  /** Content-Length of the whole body; 0 until the first response is seen. */
  readonly totalBytes: number;
  readonly receivedBytes: number;
  readonly status: DownloadStatus;
  /** §11 slug, present only on `failed`. */
  readonly errorCode?: string;
  readonly updatedAt: number;
}

export interface StorageUsage {
  /** Bytes this origin currently occupies, as the browser reports them. */
  readonly usedBytes: number;
  /** The origin's quota, or `null` when the runtime will not say. */
  readonly quotaBytes: number | null;
}

/* ── Runtime validation of persisted rows ──────────────────────────────── */

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

const DOWNLOAD_STATUSES: readonly string[] = [
  'queued',
  'downloading',
  'resuming',
  'done',
  'failed',
];

/**
 * Persisted data is INPUT: another tab, an older schema, or a half-applied
 * migration could have written something else, and `IDBObjectStore.getAll()` is
 * typed `any[]` by lib.dom (which this repo bans). Rows are therefore validated
 * rather than asserted — a malformed row is dropped, never rendered.
 */
export function isDownloadTrack(value: unknown): value is DownloadTrack {
  if (!isRecord(value)) return false;
  return (
    typeof value.trackId === 'string' &&
    typeof value.itemId === 'string' &&
    typeof value.title === 'string' &&
    typeof value.artist === 'string' &&
    typeof value.album === 'string' &&
    typeof value.albumId === 'string' &&
    typeof value.durationS === 'number' &&
    typeof value.format === 'string' &&
    typeof value.totalBytes === 'number' &&
    typeof value.receivedBytes === 'number' &&
    typeof value.status === 'string' &&
    DOWNLOAD_STATUSES.includes(value.status) &&
    typeof value.updatedAt === 'number'
  );
}

export function isDownloadItem(value: unknown): value is DownloadItem {
  if (!isRecord(value)) return false;
  return (
    typeof value.id === 'string' &&
    (value.type === 'Album' || value.type === 'Playlist' || value.type === 'Track') &&
    typeof value.title === 'string' &&
    typeof value.sub === 'string' &&
    (value.albumId === null || typeof value.albumId === 'string') &&
    Array.isArray(value.trackIds) &&
    value.trackIds.every((id) => typeof id === 'string') &&
    typeof value.createdAt === 'number'
  );
}

/**
 * Field a parked PlayEvent carries its capturing session's user id under
 * (Security W4).
 *
 * ── WHY A ROW WITHOUT IT IS DROPPED, NOT SENT ─────────────────────────────
 * `flushOfflinePlayEvents` POSTs the parked queue at boot, and the server
 * attributes a PlayEvent to WHOEVER'S SESSION DELIVERS IT. Without an owner
 * stamp, any path that changes the signed-in user without erasing this store —
 * a purge that races the download manager (Security C1), a session that merely
 * expires and is re-established as someone else on a shared device — merges
 * user A's listening history into user B's account, silently and with no audit
 * trail. Stamping at PARK time and checking at DRAIN time closes that: the two
 * moments are the only ones that know, respectively, who captured the event and
 * who is about to send it.
 *
 * It is stripped before the POST — `playEventInputSchema` is a `strictObject`
 * server-side and an unknown key would 422 the whole batch.
 *
 * A row with NO owner is dropped rather than sent. Such a row can only come
 * from a build that predates this field, and "probably the same user" is not a
 * standard worth merging someone's listening history on.
 */
export const PLAY_EVENT_OWNER_FIELD = 'ownerId';

/**
 * A parked PlayEvent. Only the fields the schema requires are checked — the
 * batch is re-validated server-side by `playEventInputSchema` anyway, and a row
 * missing its `clientEventId` is the one shape that would break the idempotent
 * merge (AC 13.7), so that is what is asserted here.
 */
export function isPlayEventInput(value: unknown): value is PlayEventInput {
  if (!isRecord(value)) return false;
  return (
    typeof value.clientEventId === 'string' &&
    value.clientEventId !== '' &&
    typeof value.trackId === 'string' &&
    typeof value.playedAt === 'string' &&
    typeof value.msPlayed === 'number' &&
    (value.source === 'stream' || value.source === 'offline')
  );
}

/* ── IndexedDB plumbing ────────────────────────────────────────────────── */

/** Promise wrapper for an IDBRequest, wired with addEventListener (CSP W3). */
function awaitRequest<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    request.addEventListener('success', () => {
      resolve(request.result);
    });
    request.addEventListener('error', () => {
      reject(request.error ?? new Error('IndexedDB request failed'));
    });
  });
}

/** Resolves when the transaction commits, so a caller can order its writes. */
function awaitTransaction(tx: IDBTransaction): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    tx.addEventListener('complete', () => {
      resolve();
    });
    tx.addEventListener('error', () => {
      reject(tx.error ?? new Error('IndexedDB transaction failed'));
    });
    tx.addEventListener('abort', () => {
      reject(tx.error ?? new Error('IndexedDB transaction aborted'));
    });
  });
}

export interface OfflineStoreDeps {
  readonly indexedDB?: IDBFactory | undefined;
  readonly caches?: CacheStorage | undefined;
  /** `navigator.storage`, for the AC 13.6 quota projection. */
  readonly storage?: StorageManager | undefined;
  readonly databaseName?: string;
  readonly now?: () => number;
  readonly warn?: (message: string, detail: Record<string, string | number>) => void;
  /**
   * How {@link OfflineStore.commitAudio} turns the chunk list into a response
   * body. INJECTED, and the injection is the whole point (Performance W13).
   *
   * The default is `new Blob(parts)`, which is what a browser should do: a Blob
   * references the chunks rather than copying them, so committing a 200 MB WAV
   * costs ~200 MB of transient heap instead of ~600 MB (the previous code held
   * the deserialized chunk array AND a `new Uint8Array(total)` concatenation of
   * it simultaneously — mobile Safari kills tabs well below that, and the quota
   * gate's bitrate table explicitly contemplates WAV).
   *
   * v1 concatenated unconditionally because a `Response` built from a FOREIGN
   * `Blob` implementation (a test double) stringifies to "[object Blob]" and
   * silently caches 15 bytes of nothing. That is a real hazard — but it is a
   * TEST-DOUBLE hazard, and the fix belongs at the seam, not in the production
   * path. A suite running without a real `Blob` passes {@link concatChunks}.
   */
  readonly buildBody?: (parts: readonly ArrayBuffer[]) => BodyInit;
}

/**
 * Copies every chunk into one contiguous buffer. The FALLBACK body builder —
 * see {@link OfflineStoreDeps.buildBody}. Exported because the test seam needs
 * it by name, and because it is the honest description of what v1 always did.
 */
export function concatChunks(parts: readonly ArrayBuffer[]): BodyInit {
  const total = parts.reduce((sum, part) => sum + part.byteLength, 0);
  const body = new Uint8Array(total);
  let offset = 0;
  for (const part of parts) {
    body.set(new Uint8Array(part), offset);
    offset += part.byteLength;
  }
  return body;
}

export interface OfflineStore {
  /** True when this runtime has an IndexedDB at all (jsdom/private mode: no). */
  readonly available: boolean;

  listItems(): Promise<DownloadItem[]>;
  putItem(item: DownloadItem): Promise<void>;
  /** Removes the row, its track records, its partials and its cached bytes. */
  deleteItem(id: string): Promise<void>;

  listTracks(): Promise<DownloadTrack[]>;
  getTrack(trackId: string): Promise<DownloadTrack | null>;
  putTrack(track: DownloadTrack): Promise<void>;

  /** Bytes already downloaded for `trackId`; 0 when nothing is parked. */
  partialLength(trackId: string): Promise<number>;
  /** Appends a chunk to the resume buffer and returns the new total. */
  appendPartial(trackId: string, chunk: ArrayBuffer): Promise<number>;
  clearPartial(trackId: string): Promise<void>;

  /**
   * Seals the resume buffer into Cache Storage as the complete body for
   * `/api/stream/<trackId>` and drops the partial. Returns false when there is
   * nothing to commit — this is the ONLY way a track becomes playable offline.
   */
  commitAudio(trackId: string, contentType: string): Promise<boolean>;
  /** Whether a COMPLETE body is cached for this track. */
  hasAudio(trackId: string): Promise<boolean>;
  /** Track ids with a complete cached body. */
  cachedTrackIds(): Promise<string[]>;

  /** Stores a fetched cover under `/api/art/<albumId>` for offline paint. */
  putArt(albumId: string, response: Response): Promise<void>;
  hasArt(albumId: string): Promise<boolean>;

  /** Best-effort catalog cache (AC 13.8's "broader cache is best-effort"). */
  putCatalog(key: string, value: unknown): Promise<void>;
  getCatalog(key: string): Promise<unknown>;

  /**
   * Offline PlayEvents (AC 13.7). Deduplicated by `clientEventId`, and BOUND
   * TO THE CAPTURING SESSION by `ownerId` — see {@link PLAY_EVENT_OWNER_FIELD}.
   */
  appendPlayEvents(events: readonly PlayEventInput[], ownerId: string | null): Promise<void>;
  /**
   * Drains up to `limit` events belonging to `ownerId`.
   *
   * Rows stamped with a DIFFERENT owner are deleted rather than returned: they
   * are user A's listening history sitting in user B's session, and the only
   * safe thing to do with them is destroy them. Rows with NO owner predate the
   * stamping and are treated the same way (see the field's header).
   */
  drainPlayEvents(limit: number, ownerId: string | null): Promise<PlayEventInput[]>;
  /** Puts drained events back after a failed flush (bounded, oldest dropped). */
  restorePlayEvents(events: readonly PlayEventInput[], ownerId: string | null): Promise<void>;
  countPlayEvents(): Promise<number>;

  /** The `telemetry-client.ts` seam this module supersedes the interim one with. */
  readonly telemetryBuffer: TelemetryBuffer;

  /** `navigator.storage.estimate()`, normalised. */
  usage(): Promise<StorageUsage>;

  /** Erases every download: records, partials, cached audio and art. */
  deleteAllDownloads(): Promise<void>;

  /** Releases the cached connection (tests, and `dispose`). */
  close(): void;
}

/**
 * Builds an offline store over the injected platform APIs.
 *
 * EVERY method degrades to a no-op / empty result when the runtime has no
 * IndexedDB or no Cache Storage. That is deliberate and it is not "silently
 * swallowing": offline persistence is an enhancement, and a hardened browser
 * (or a private window, or jsdom) must get a working online app rather than a
 * boot failure. The `available` flag lets the UI say so honestly.
 */
export function createOfflineStore(deps: OfflineStoreDeps = {}): OfflineStore {
  const scope = globalThis as unknown as {
    indexedDB?: IDBFactory;
    caches?: CacheStorage;
    navigator?: { storage?: StorageManager };
  };
  const {
    indexedDB: factory = scope.indexedDB,
    caches: cacheStorage = scope.caches,
    storage = scope.navigator?.storage,
    databaseName = OFFLINE_DB_NAME,
    now = () => Date.now(),
    warn = (message, detail) => {
      console.warn(message, detail);
    },
    buildBody: buildAudioBody = (parts) => new Blob(parts as ArrayBuffer[]),
  } = deps;

  const hasIdb = factory !== undefined && factory !== null;
  let connection: IDBDatabase | null = null;
  let opening: Promise<IDBDatabase> | null = null;
  /**
   * Monotonic write counter for the LRU.
   *
   * IT CANNOT BE THE KEY ORDER, and that is the whole reason it exists.
   * `IDBObjectStore.getAllKeys()` returns KEY order, not insertion order — so
   * for a store keyed by a catalog id (or a track uuid) "the first key" is
   * simply the alphabetically smallest one, which has nothing to do with how
   * recently it was used. Eviction therefore reads the stored ordering field.
   * A counter rather than `now()` because two writes inside the same
   * millisecond are common and would tie.
   *
   * IT IS SEEDED FROM THE STORE, not from zero. Restarting at 0 on every page
   * load while persisted rows carry seqs from prior sessions INVERTS the LRU:
   * the newest writes get the lowest numbers and are evicted first, so the
   * cache throws away exactly what the user is looking at. `seedWriteSeq` runs
   * once per connection, before the first eviction decision can be made.
   */
  let writeSeq = 0;
  /** Whether {@link seedWriteSeq} has run for the current connection. */
  let writeSeqSeeded = false;

  /**
   * Lifts {@link writeSeq} above every persisted `seq`, once. Best-effort: a
   * failure leaves the counter at 0, which is the pre-fix behaviour rather
   * than a broken store.
   */
  async function seedWriteSeq(): Promise<void> {
    if (writeSeqSeeded) return;
    writeSeqSeeded = true;
    const highest = await withStore<number>(
      STORE_CATALOG,
      'readonly',
      async (stores) => {
        const rows: unknown = await awaitRequest(requireStore(stores, STORE_CATALOG).getAll());
        if (!Array.isArray(rows)) return 0;
        let max = 0;
        for (const row of rows) {
          if (isRecord(row) && typeof row.seq === 'number' && row.seq > max) max = row.seq;
        }
        return max;
      },
      0,
    );
    if (highest > writeSeq) writeSeq = highest;
  }

  function open(): Promise<IDBDatabase> {
    if (connection !== null) return Promise.resolve(connection);
    if (opening !== null) return opening;
    if (!hasIdb) return Promise.reject(new Error('offline-store: no IndexedDB in this runtime'));

    const request = factory.open(databaseName, OFFLINE_DB_VERSION);
    request.addEventListener('upgradeneeded', (event) => {
      const db = request.result;
      const from = event.oldVersion;

      /*
       * v1 → v2: the partials store changes SHAPE (one row per track holding
       * every chunk -> one row per chunk). The old rows cannot be read by the
       * new code and converting them would run the very O(n²) path this
       * migration removes, so they are dropped. See OFFLINE_DB_VERSION's
       * header for why discarding an unfinished download is lossless.
       */
      if (from >= 1 && from < 2 && db.objectStoreNames.contains(STORE_PARTIALS)) {
        db.deleteObjectStore(STORE_PARTIALS);
      }

      for (const name of OFFLINE_DB_STORES) {
        if (db.objectStoreNames.contains(name)) continue;
        // `telemetry` and `playEvents` are QUEUES — insertion-ordered keys, so
        // an auto-increment key is exactly the FIFO the drains rely on. The
        // rest are keyed by a field of the record itself.
        if (name === STORE_TELEMETRY) db.createObjectStore(name, { autoIncrement: true });
        else if (name === STORE_PLAY_EVENTS) {
          db.createObjectStore(name, { keyPath: 'clientEventId' });
        } else if (name === STORE_ITEMS) db.createObjectStore(name, { keyPath: 'id' });
        else if (name === STORE_TRACKS) db.createObjectStore(name, { keyPath: 'trackId' });
        else db.createObjectStore(name);
      }
    });

    opening = awaitRequest(request)
      .then((db) => {
        /*
         * THE PURGE MUST WIN. A cached connection makes
         * `indexedDB.deleteDatabase` fire `blocked`, which `localPurge` records
         * as a failure and abandons — i.e. a sign-out would leave the user's
         * downloads on disk. Closing on `versionchange` (which a delete
         * request raises on every other connection) lets the delete proceed.
         */
        db.addEventListener('versionchange', () => {
          db.close();
          connection = null;
        });
        db.addEventListener('close', () => {
          connection = null;
        });
        connection = db;
        return db;
      })
      .finally(() => {
        opening = null;
      });
    return opening;
  }

  /**
   * READ, then WRITE — in two separate transactions, deliberately (QA W5).
   *
   * ── WHY NOT ONE TRANSACTION ───────────────────────────────────────────
   * Every bounded store here is a read-modify-write: read the rows, decide
   * which to evict, then delete and put. The obvious shape is one `readwrite`
   * transaction with `await awaitRequest(getAll())` in the middle — and it is
   * WRONG ACROSS BROWSERS.
   *
   * A real IndexedDB transaction is active during the task that created it and
   * during each request-event callback it dispatches. The continuation of an
   * `await` on a request lands in a microtask scheduled by that callback, i.e.
   * AFTER it returned. Chrome and Firefox still consider the transaction active
   * there; WebKit does not, and raises `TransactionInactiveError` — so the
   * one-transaction form works everywhere the code was tested and fails on
   * iOS, which is a first-class target for this whole feature. (The test fake
   * now models the strict behaviour, which is what surfaced this.)
   *
   * ── WHAT IS GIVEN UP, AND WHY IT IS FINE ──────────────────────────────
   * Atomicity across the read and the write. Two tabs trimming the same LRU
   * concurrently could evict one row too many or too few; two tabs draining the
   * play-event queue could both return the same event. Neither matters here:
   * the LRU bound is a ceiling, not an invariant, and a doubly-drained
   * PlayEvent is deduplicated by `clientEventId` server-side (AC 13.7's whole
   * design). Nothing in this file has a cross-row invariant that a torn
   * read-write would break — the one thing that genuinely must be atomic is a
   * finished audio body, and that is `cache.put`, not IndexedDB.
   *
   * @param names stores both phases open
   * @param read issues its requests and resolves with what the write needs
   * @param write issues writes SYNCHRONOUSLY — it must not await
   * @param fallback returned on any failure
   */
  async function readThenWrite<R>(
    names: string | string[],
    read: (stores: Record<string, IDBObjectStore>) => Promise<R>,
    write: (stores: Record<string, IDBObjectStore>, value: R) => void,
    fallback: R,
  ): Promise<R> {
    const value = await withStore(names, 'readonly', read, fallback);
    await withStore(
      names,
      'readwrite',
      (stores) => {
        write(stores, value);
        return null;
      },
      null,
    );
    return value;
  }

  /**
   * Runs `work` inside one transaction. Resolves to `fallback` on ANY failure
   * — see the factory's header for why a storage fault must not break the app.
   *
   * A `work` function that AWAITS between requests is only safe if every
   * request it issues after the await is issued from the same task — see
   * {@link readThenWrite} for why, and use that instead for read-modify-write.
   */
  async function withStore<T>(
    names: string | string[],
    mode: IDBTransactionMode,
    // `T | Promise<T>`: several writes are pure `store.put(...)` calls with
    // nothing to await, and forcing them through an `async` wrapper would be
    // ceremony (and a lint error) for no behaviour.
    work: (stores: Record<string, IDBObjectStore>) => T | Promise<T>,
    fallback: T,
  ): Promise<T> {
    if (!hasIdb) return fallback;
    let db: IDBDatabase;
    try {
      db = await open();
    } catch (error) {
      warn('offline-store: could not open the database', { reason: reasonOf(error) });
      return fallback;
    }
    try {
      const list = typeof names === 'string' ? [names] : names;
      const tx = db.transaction(list, mode);
      const stores: Record<string, IDBObjectStore> = {};
      for (const name of list) stores[name] = tx.objectStore(name);
      const result = await work(stores);
      if (mode === 'readwrite') await awaitTransaction(tx);
      return result;
    } catch (error) {
      warn('offline-store: transaction failed', { reason: reasonOf(error), mode });
      return fallback;
    }
  }

  function reasonOf(error: unknown): string {
    if (error instanceof Error) return error.name === '' ? 'Error' : error.name;
    return typeof error;
  }

  function requireStore(stores: Record<string, IDBObjectStore>, name: string): IDBObjectStore {
    const store = stores[name];
    if (store === undefined) throw new Error(`offline-store: store "${name}" was not opened`);
    return store;
  }

  /* ── Cache Storage helpers ───────────────────────────────────────────── */

  const hasCaches = cacheStorage !== undefined && cacheStorage !== null;

  /** Same-origin path the SW intercepts — never a `blob:` URL (Security N1). */
  function audioPath(trackId: string): string {
    return `/api/stream/${encodeURIComponent(trackId)}`;
  }
  function artPath(albumId: string): string {
    return `/api/art/${encodeURIComponent(albumId)}`;
  }

  async function withCache<T>(
    name: string,
    work: (cache: Cache) => Promise<T>,
    fallback: T,
  ): Promise<T> {
    if (!hasCaches) return fallback;
    try {
      return await work(await cacheStorage.open(name));
    } catch (error) {
      warn('offline-store: cache operation failed', { reason: reasonOf(error), cache: name });
      return fallback;
    }
  }

  /* ── Telemetry buffer (supersedes telemetry-client's interim one) ────── */

  const telemetryBuffer: TelemetryBuffer = {
    async append(events) {
      await readThenWrite<IDBValidKey[]>(
        STORE_TELEMETRY,
        async (stores) => await awaitRequest(requireStore(stores, STORE_TELEMETRY).getAllKeys()),
        (stores, keys) => {
          const store = requireStore(stores, STORE_TELEMETRY);
          // Bounded: trim from the FRONT so recent events win. Auto-increment
          // keys ARE insertion order here, which is why this one queue can use
          // key order where the others cannot (see `writeSeq`). The incoming
          // batch is clamped too, so one oversized append cannot sail past the
          // ceiling on an empty store.
          const admitted = events.slice(-MAX_QUEUED);
          const overflow = keys.length + admitted.length - MAX_QUEUED;
          for (let i = 0; i < overflow && i < keys.length; i += 1) {
            const key = keys[i];
            if (key !== undefined) store.delete(key);
          }
          for (const event of admitted) store.add(event);
        },
        [],
      );
    },

    async drain(limit) {
      const drained = await readThenWrite<{
        keys: IDBValidKey[];
        events: BufferedTelemetryEvent[];
      }>(
        STORE_TELEMETRY,
        async (stores) => {
          const store = requireStore(stores, STORE_TELEMETRY);
          /*
           * Both requests are issued AND both listeners attached before either
           * is awaited. Attaching the second listener after awaiting the first
           * would miss its `success` entirely — the event has already fired by
           * then and nothing replays it — so the promise would never settle.
           * (Issuing them in one task is also what keeps the transaction
           * active for both; see `readThenWrite`.)
           */
          const keysPromise = awaitRequest(store.getAllKeys());
          const rowsPromise = awaitRequest(store.getAll());
          const keys = (await keysPromise).slice(0, limit);
          const rows: unknown = await rowsPromise;
          const events: BufferedTelemetryEvent[] = [];
          if (Array.isArray(rows)) {
            for (const row of rows.slice(0, limit)) {
              // Shape-checked in telemetry-client; anything else is dropped by
              // the client's own `isTelemetryEventName` before it can be sent.
              if (isRecord(row) && typeof row.event === 'string') {
                events.push(row as unknown as BufferedTelemetryEvent);
              }
            }
          }
          return { keys, events };
        },
        (stores, { keys }) => {
          const store = requireStore(stores, STORE_TELEMETRY);
          for (const key of keys) store.delete(key);
        },
        { keys: [], events: [] },
      );
      return drained.events;
    },
  };

  /* ── Public surface ──────────────────────────────────────────────────── */

  const store: OfflineStore = {
    available: hasIdb,
    telemetryBuffer,

    async listItems() {
      return await withStore<DownloadItem[]>(
        STORE_ITEMS,
        'readonly',
        async (stores) => {
          const rows: unknown = await awaitRequest(requireStore(stores, STORE_ITEMS).getAll());
          if (!Array.isArray(rows)) return [];
          return rows.filter(isDownloadItem).sort((a, b) => a.createdAt - b.createdAt);
        },
        [],
      );
    },

    async putItem(item) {
      await readThenWrite<DownloadItem[]>(
        STORE_ITEMS,
        async (stores) => {
          const rows: unknown = await awaitRequest(requireStore(stores, STORE_ITEMS).getAll());
          return (
            (Array.isArray(rows) ? rows : [])
              .filter(isDownloadItem)
              .filter((row) => row.id !== item.id)
              // OLDEST FIRST — by `createdAt`, not by key order (see `writeSeq`).
              .sort((a, b) => a.createdAt - b.createdAt)
          );
        },
        (stores, existing) => {
          const objectStore = requireStore(stores, STORE_ITEMS);
          const overflow = existing.length + 1 - MAX_DOWNLOAD_ITEMS;
          for (let i = 0; i < overflow && i < existing.length; i += 1) {
            const doomed = existing[i];
            if (doomed !== undefined) objectStore.delete(doomed.id);
          }
          objectStore.put(item);
        },
        [],
      );
    },

    async deleteItem(id) {
      const allTracks = await store.listTracks();
      const tracks = allTracks.filter((track) => track.itemId === id);
      const survivors = allTracks.filter((track) => track.itemId !== id);

      await readThenWrite<IDBValidKey[]>(
        [STORE_ITEMS, STORE_TRACKS, STORE_PARTIALS],
        // v2 partials are one row PER CHUNK, so a track's buffer is a key
        // range, not a key. Keys only — no body is deserialized to drop it.
        async (stores) => await awaitRequest(requireStore(stores, STORE_PARTIALS).getAllKeys()),
        (stores, keys) => {
          requireStore(stores, STORE_ITEMS).delete(id);
          const trackStore = requireStore(stores, STORE_TRACKS);
          const partialStore = requireStore(stores, STORE_PARTIALS);
          for (const track of tracks) trackStore.delete(track.trackId);
          for (const key of keys) {
            if (tracks.some((track) => isPartialKeyFor(key, track.trackId))) {
              partialStore.delete(key);
            }
          }
        },
        [],
      );

      // Freeing real storage is the point of the delete (AC 13.3) — the record
      // going away without the bytes would be a lie the storage bar tells.
      await withCache(
        AUDIO_CACHE_NAME,
        async (cache) => {
          for (const track of tracks) await cache.delete(audioPath(track.trackId));
          return null;
        },
        null,
      );

      /*
       * ...and the ART too. Deleting the audio while leaving the cover behind
       * made the storage bar dishonest: art accumulated until `removeAll` or a
       * purge, so a user who deleted every download one row at a time still saw
       * (and still occupied) bytes they had explicitly asked to free.
       *
       * Only for albums NO SURVIVING track still references — two downloads of
       * the same album, or an album row plus a single-track row from it, share
       * one cached cover, and deleting one must not blank the other's art.
       */
      const stillReferenced = new Set(survivors.map((track) => track.albumId));
      const doomedAlbums = [...new Set(tracks.map((track) => track.albumId))].filter(
        (albumId) => albumId !== '' && !stillReferenced.has(albumId),
      );
      if (doomedAlbums.length === 0) return;
      await withCache(
        ART_CACHE_NAME,
        async (cache) => {
          for (const albumId of doomedAlbums) await cache.delete(artPath(albumId));
          return null;
        },
        null,
      );
    },

    async listTracks() {
      return await withStore<DownloadTrack[]>(
        STORE_TRACKS,
        'readonly',
        async (stores) => {
          const rows: unknown = await awaitRequest(requireStore(stores, STORE_TRACKS).getAll());
          return Array.isArray(rows) ? rows.filter(isDownloadTrack) : [];
        },
        [],
      );
    },

    async getTrack(trackId) {
      return await withStore<DownloadTrack | null>(
        STORE_TRACKS,
        'readonly',
        async (stores) => {
          const row: unknown = await awaitRequest(
            requireStore(stores, STORE_TRACKS).get(trackId) as IDBRequest<unknown>,
          );
          return isDownloadTrack(row) ? row : null;
        },
        null,
      );
    },

    async putTrack(track) {
      await withStore(
        STORE_TRACKS,
        'readwrite',
        (stores) => {
          requireStore(stores, STORE_TRACKS).put({ ...track, updatedAt: now() });
          return null;
        },
        null,
      );
    },

    async partialLength(trackId) {
      /*
       * O(1): ONE key read of the meta record. v1 summed a deserialized copy
       * of every chunk — see OFFLINE_DB_VERSION.
       */
      return await withStore<number>(
        STORE_PARTIALS,
        'readonly',
        async (stores) => {
          const row: unknown = await awaitRequest(
            requireStore(stores, STORE_PARTIALS).get(
              partialMetaKey(trackId),
            ) as IDBRequest<unknown>,
          );
          return isPartialMeta(row) ? row.bytes : 0;
        },
        0,
      );
    },

    async appendPartial(trackId, chunk) {
      /*
       * THE HOT PATH (Performance C2). One meta read, one chunk `add`, one meta
       * `put` — all three O(1) in the bytes already received. The chunk itself
       * is never read back until `commitAudio`, so a 100 MB track costs 100 MB
       * of writes rather than ~5 GB.
       *
       * The meta read/modify/write is three requests on ONE transaction with no
       * `await` between the read's completion and the writes being ISSUED —
       * see `withStore`'s note on transaction lifetime for why that matters on
       * WebKit.
       */
      const next = await readThenWrite<PartialMeta | null>(
        STORE_PARTIALS,
        async (stores) => {
          const row: unknown = await awaitRequest(
            requireStore(stores, STORE_PARTIALS).get(
              partialMetaKey(trackId),
            ) as IDBRequest<unknown>,
          );
          const meta: PartialMeta = isPartialMeta(row) ? row : { bytes: 0, nextSeq: 0 };
          if (meta.nextSeq >= MAX_PARTIAL_CHUNKS) {
            // Bounded like every other queue here. Refusing is safe: the caller
            // sees the byte count stop advancing and fails the track, and the
            // partial stays resumable rather than growing without limit.
            return null;
          }
          return { bytes: meta.bytes + chunk.byteLength, nextSeq: meta.nextSeq + 1 };
        },
        (stores, meta) => {
          if (meta === null) return;
          const objectStore = requireStore(stores, STORE_PARTIALS);
          objectStore.add(chunk, partialChunkKey(trackId, meta.nextSeq - 1));
          objectStore.put(meta, partialMetaKey(trackId));
        },
        null,
      );
      return next?.bytes ?? 0;
    },

    async clearPartial(trackId) {
      await readThenWrite<IDBValidKey[]>(
        STORE_PARTIALS,
        // Keys only — `getAllKeys` never deserializes a body, so dropping a
        // 100 MB partial does not read 100 MB to do it.
        async (stores) => await awaitRequest(requireStore(stores, STORE_PARTIALS).getAllKeys()),
        (stores, keys) => {
          const objectStore = requireStore(stores, STORE_PARTIALS);
          for (const key of keys) {
            if (isPartialKeyFor(key, trackId)) objectStore.delete(key);
          }
        },
        [],
      );
    },

    async commitAudio(trackId, contentType) {
      /*
       * TWO READ PHASES, not one. The keys cannot be known without awaiting
       * `getAllKeys`, and that await ends the transaction's activity window —
       * so the `get`s have to be issued against a FRESH transaction, where
       * they are all issued in one task before any is awaited. See
       * `readThenWrite` for the underlying rule.
       */
      const keys = await withStore<IDBValidKey[]>(
        STORE_PARTIALS,
        'readonly',
        async (stores) =>
          (await awaitRequest(requireStore(stores, STORE_PARTIALS).getAllKeys())).filter((key) =>
            isPartialChunkKeyFor(key, trackId),
          ),
        [],
      );
      if (keys.length === 0) return false;

      const parts = await withStore<ArrayBuffer[]>(
        STORE_PARTIALS,
        'readonly',
        async (stores) => {
          const objectStore = requireStore(stores, STORE_PARTIALS);
          /*
           * Every `get` is issued AND wrapped before any is awaited. Both
           * halves matter: issuing them in one task keeps the transaction
           * active (see `readThenWrite`), and attaching every listener up front
           * is what stops a request whose `success` has already fired from
           * hanging forever. The implementation is also free to service them
           * in parallel.
           */
          const pending = keys.map((key) =>
            awaitRequest(objectStore.get(key) as IDBRequest<unknown>),
          );
          const chunks: ArrayBuffer[] = [];
          for (const request of pending) {
            const chunk = asArrayBuffer(await request);
            if (chunk !== null) chunks.push(chunk);
          }
          return chunks;
        },
        [],
      );
      if (parts.length === 0) return false;

      const total = parts.reduce((sum, part) => sum + part.byteLength, 0);
      const committed = await withCache(
        AUDIO_CACHE_NAME,
        async (cache) => {
          // ATOMIC: the entry appears only once the whole body is assembled —
          // this is what makes "partial is never playable" structural (AC 13.4).
          await cache.put(
            audioPath(trackId),
            new Response(buildAudioBody(parts), {
              status: 200,
              headers: {
                'content-type': contentType,
                'content-length': String(total),
                // Answered by the SW from Cache Storage; never revalidated.
                'cache-control': 'private, no-store',
              },
            }),
          );
          return true;
        },
        false,
      );
      if (committed) await store.clearPartial(trackId);
      return committed;
    },

    async hasAudio(trackId) {
      return await withCache(
        AUDIO_CACHE_NAME,
        async (cache) => (await cache.match(audioPath(trackId))) !== undefined,
        false,
      );
    },

    async cachedTrackIds() {
      return await withCache<string[]>(
        AUDIO_CACHE_NAME,
        async (cache) => {
          const ids: string[] = [];
          for (const request of await cache.keys()) {
            /*
             * PER-KEY, not per-cache. `decodeURIComponent` throws URIError on a
             * malformed escape, and one such key inside the loop used to reject
             * the whole `withCache`, which swallowed it to `[]` — so a single
             * bad entry silently reported that NOTHING was downloaded and every
             * offline track was marked unavailable. One key's failure now costs
             * one key.
             */
            try {
              const path = new URL(request.url, 'http://localhost').pathname;
              if (!path.startsWith('/api/stream/')) continue;
              const id = decodeURIComponent(path.slice('/api/stream/'.length));
              if (id !== '') ids.push(id);
            } catch {
              /* a key this app did not write; the rest of the cache is fine */
            }
          }
          return ids;
        },
        [],
      );
    },

    async putArt(albumId, response) {
      await withCache(
        ART_CACHE_NAME,
        async (cache) => {
          await cache.put(artPath(albumId), response);
          return null;
        },
        null,
      );
    },

    async hasArt(albumId) {
      return await withCache(
        ART_CACHE_NAME,
        async (cache) => (await cache.match(artPath(albumId))) !== undefined,
        false,
      );
    },

    async putCatalog(key, value) {
      // Before the FIRST eviction decision — see `writeSeq`'s header.
      await seedWriteSeq();
      await readThenWrite<Array<{ key: string; seq: number }>>(
        STORE_CATALOG,
        async (stores) => {
          const rows: unknown = await awaitRequest(requireStore(stores, STORE_CATALOG).getAll());
          // TRUE LRU: ordered by the write sequence stored on each row, and a
          // re-put of an existing key refreshes it (so it stops being a
          // candidate). Key order would evict alphabetically — see `writeSeq`.
          return (Array.isArray(rows) ? rows : [])
            .filter(
              (row): row is { key: string; seq: number } =>
                isRecord(row) && typeof row.key === 'string' && typeof row.seq === 'number',
            )
            .filter((row) => row.key !== key)
            .sort((a, b) => a.seq - b.seq);
        },
        (stores, existing) => {
          const objectStore = requireStore(stores, STORE_CATALOG);
          const overflow = existing.length + 1 - MAX_CATALOG_ENTRIES;
          for (let i = 0; i < overflow && i < existing.length; i += 1) {
            const doomed = existing[i];
            if (doomed !== undefined) objectStore.delete(doomed.key);
          }
          writeSeq += 1;
          objectStore.put({ key, value, updatedAt: now(), seq: writeSeq }, key);
        },
        [],
      );
    },

    async getCatalog(key) {
      return await withStore<unknown>(
        STORE_CATALOG,
        'readonly',
        async (stores) => {
          const row: unknown = await awaitRequest(
            requireStore(stores, STORE_CATALOG).get(key) as IDBRequest<unknown>,
          );
          return isRecord(row) ? row.value : undefined;
        },
        undefined,
      );
    },

    async appendPlayEvents(events, ownerId) {
      if (events.length === 0) return;
      // Security W4 — the stamp goes on at PARK time, which is the only moment
      // that knows whose session captured the play.
      const owned = events.map((event) => ({ ...event, [PLAY_EVENT_OWNER_FIELD]: ownerId }));
      const admitted = owned.slice(-MAX_PLAY_EVENTS);
      await readThenWrite<PlayEventInput[]>(
        STORE_PLAY_EVENTS,
        async (stores) => {
          const objectStore = requireStore(stores, STORE_PLAY_EVENTS);
          const rows: unknown = await awaitRequest(objectStore.getAll());
          const incomingIds = new Set(events.map((event) => event.clientEventId));
          // The BATCH is bounded too, not just the store: a single
          // `appendPlayEvents` of more than the ceiling would otherwise sail
          // past it, because there are no existing rows to evict.
          return (
            (Array.isArray(rows) ? rows : [])
              .filter(isPlayEventInput)
              .filter((row) => !incomingIds.has(row.clientEventId))
              // OLDEST FIRST by the event's own timestamp. Key order here is
              // uuid order, which is meaningless — see `writeSeq`.
              .sort((a, b) => a.playedAt.localeCompare(b.playedAt))
          );
        },
        (stores, existing) => {
          const objectStore = requireStore(stores, STORE_PLAY_EVENTS);
          const overflow = existing.length + admitted.length - MAX_PLAY_EVENTS;
          for (let i = 0; i < overflow && i < existing.length; i += 1) {
            const doomed = existing[i];
            if (doomed !== undefined) objectStore.delete(doomed.clientEventId);
          }
          // `put`, keyed on `clientEventId`: parking the same event twice (a
          // failed flush that is retried) stores ONE row, so the local queue
          // cannot inflate the counts the server later dedupes (AC 13.7).
          for (const event of admitted) objectStore.put(event);
        },
        [],
      );
    },

    async drainPlayEvents(limit, ownerId) {
      const plan = await readThenWrite<{ deliver: PlayEventInput[]; discard: string[] }>(
        STORE_PLAY_EVENTS,
        async (stores) => {
          const rows: unknown = await awaitRequest(
            requireStore(stores, STORE_PLAY_EVENTS).getAll(),
          );
          const healthy: PlayEventInput[] = [];
          const discard: string[] = [];
          if (Array.isArray(rows)) {
            for (const row of rows) {
              if (!isRecord(row) || typeof row.clientEventId !== 'string') continue;
              /*
               * Read the owner (and strip it) BEFORE the schema check: after
               * `isPlayEventInput` narrows the row to `PlayEventInput` the
               * field is no longer addressable, and it is not part of that
               * type precisely because the server has never heard of it.
               */
              const { [PLAY_EVENT_OWNER_FIELD]: owner, ...bare } = row;
              if (!isPlayEventInput(bare)) {
                // A row that cannot satisfy the schema would 422 the WHOLE
                // batch and lose every healthy event travelling with it.
                discard.push(row.clientEventId);
                continue;
              }
              /*
               * SECURITY W4 — the owner check. A row captured by another
               * session is DESTROYED, not delivered: sending it would merge
               * one user's listening history into another's account. A row
               * with no stamp at all is treated identically (see
               * PLAY_EVENT_OWNER_FIELD).
               */
              if (typeof owner !== 'string' || owner !== ownerId) {
                discard.push(row.clientEventId);
                continue;
              }
              healthy.push(bare);
            }
          }
          // CHRONOLOGICAL, not key order — the batch the server absorbs should
          // read the way the listening happened.
          const deliver = healthy
            .sort((a, b) => a.playedAt.localeCompare(b.playedAt))
            .slice(0, limit);
          return { deliver, discard };
        },
        (stores, { deliver, discard }) => {
          const objectStore = requireStore(stores, STORE_PLAY_EVENTS);
          for (const id of discard) objectStore.delete(id);
          for (const event of deliver) objectStore.delete(event.clientEventId);
        },
        { deliver: [], discard: [] },
      );
      return plan.deliver;
    },

    async restorePlayEvents(events, ownerId) {
      await store.appendPlayEvents(events, ownerId);
    },

    async countPlayEvents() {
      return await withStore<number>(
        STORE_PLAY_EVENTS,
        'readonly',
        async (stores) =>
          (await awaitRequest(requireStore(stores, STORE_PLAY_EVENTS).getAllKeys())).length,
        0,
      );
    },

    async usage() {
      if (storage === undefined || storage === null || typeof storage.estimate !== 'function') {
        return { usedBytes: 0, quotaBytes: null };
      }
      try {
        const estimate = await storage.estimate();
        return {
          usedBytes: typeof estimate.usage === 'number' ? estimate.usage : 0,
          quotaBytes: typeof estimate.quota === 'number' ? estimate.quota : null,
        };
      } catch (error) {
        warn('offline-store: storage estimate failed', { reason: reasonOf(error) });
        return { usedBytes: 0, quotaBytes: null };
      }
    },

    async deleteAllDownloads() {
      await withStore(
        [STORE_ITEMS, STORE_TRACKS, STORE_PARTIALS],
        'readwrite',
        (stores) => {
          requireStore(stores, STORE_ITEMS).clear();
          requireStore(stores, STORE_TRACKS).clear();
          requireStore(stores, STORE_PARTIALS).clear();
          return null;
        },
        null,
      );
      if (!hasCaches) return;
      try {
        // Dropping the whole cache frees the bytes in one call; the next
        // `cache.open` recreates it empty.
        await cacheStorage.delete(AUDIO_CACHE_NAME);
        await cacheStorage.delete(ART_CACHE_NAME);
      } catch (error) {
        warn('offline-store: could not delete the download caches', { reason: reasonOf(error) });
      }
    },

    close() {
      connection?.close();
      connection = null;
    },
  };

  return store;
}

/** The process-wide store; `modules/app/index.js` wires this one. */
export const offlineStore: OfflineStore = createOfflineStore();
